// src/firebase.js
// ----------------------------------------------------
// Firebase init + Auth + Firestore helpers (production-ready)
// ----------------------------------------------------
import { initializeApp, getApps } from "firebase/app";
import {
  getAuth,
  onAuthStateChanged,
  GoogleAuthProvider,
  signInWithPopup,
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  updateProfile as fbUpdateProfile,
  signOut as fbSignOut,
} from "firebase/auth";
import {
  getFirestore,
  enableIndexedDbPersistence,
  doc,
  getDoc,
  setDoc,
  serverTimestamp
  // ⬅ Removed Timestamp (unused → caused warning)
} from "firebase/firestore";

/* ---------------------------
   Environment helpers
   --------------------------- */
const requiredEnv = (name) => {
  const v = process.env[name];
  if (!v) {
    console.warn(`[firebase] Missing env var ${name}. See .env.example`);
  }
  return v || "";
};

const firebaseConfig = {
  apiKey: requiredEnv("REACT_APP_FIREBASE_API_KEY"),
  authDomain: requiredEnv("REACT_APP_FIREBASE_AUTH_DOMAIN"),
  projectId: requiredEnv("REACT_APP_FIREBASE_PROJECT_ID"),
  storageBucket: requiredEnv("REACT_APP_FIREBASE_STORAGE_BUCKET"),
  messagingSenderId: requiredEnv("REACT_APP_FIREBASE_MESSAGING_SENDER_ID"),
  appId: requiredEnv("REACT_APP_FIREBASE_APP_ID"),
};

/* ---------------------------
   Initialize (safe, single init)
   --------------------------- */
const app = getApps().length ? getApps()[0] : initializeApp(firebaseConfig);

export const auth = getAuth(app);
export const db = getFirestore(app);

/* ---------------------------
   IndexedDB persistence (best-effort)
   --------------------------- */
(async function enablePersistenceSafely() {
  try {
    await enableIndexedDbPersistence(db);
    console.debug("[firebase] IndexedDB persistence enabled");
  } catch (err) {
    if (err?.code === "failed-precondition") {
      console.warn("[firebase] Persistence disabled: multiple tabs open.");
    } else if (err?.code === "unimplemented") {
      console.warn("[firebase] Persistence not available in this browser.");
    } else {
      console.warn("[firebase] Persistence error:", err?.message || err);
    }
  }
})();

/* ---------------------------
   Auth provider
   --------------------------- */
const googleProvider = new GoogleAuthProvider();
googleProvider.setCustomParameters({ prompt: "select_account" });

/* ---------------------------
   Firestore helpers
   --------------------------- */
export async function ensureUserProfile(user, extra = {}) {
  if (!user || !user.uid) return null;
  try {
    const ref = doc(db, "users", user.uid);
    const snap = await getDoc(ref);
    const base = {
      uid: user.uid,
      name: user.displayName || "",
      email: user.email || "",
      phone: user.phoneNumber || "",
      photoURL: user.photoURL || "",
    };

    if (!snap.exists()) {
      await setDoc(
        ref,
        { ...base, ...extra, createdAt: serverTimestamp() },
        { merge: true }
      );
    } else {
      await setDoc(
        ref,
        { ...extra, lastLoginAt: serverTimestamp() },
        { merge: true }
      );
    }
    return (await getDoc(ref)).data();
  } catch (err) {
    console.error("[firebase] ensureUserProfile error:", err?.message || err);
    throw err;
  }
}

export async function getUserDoc(uid) {
  if (!uid) return null;
  const ref = doc(db, "users", uid);
  const snap = await getDoc(ref);
  return snap.exists() ? snap.data() : null;
}

/* ---------------------------
   Auth helpers
   --------------------------- */
export async function signUpWithEmail({ email, password, name, phone }) {
  try {
    const cred = await createUserWithEmailAndPassword(auth, email, password);
    if (name) {
      await fbUpdateProfile(cred.user, { displayName: name });
    }
    await ensureUserProfile(cred.user, { name: name || "", phone: phone || "" });
    return cred.user;
  } catch (err) {
    console.error("[firebase] signUpWithEmail error:", err?.message || err);
    throw err;
  }
}

export async function signInWithEmail({ email, password }) {
  try {
    const cred = await signInWithEmailAndPassword(auth, email, password);
    await ensureUserProfile(cred.user);
    return cred.user;
  } catch (err) {
    console.error("[firebase] signInWithEmail error:", err?.message || err);
    throw err;
  }
}

export async function signInWithGoogle() {
  try {
    const cred = await signInWithPopup(auth, googleProvider);
    await ensureUserProfile(cred.user);
    return cred.user;
  } catch (err) {
    console.error("[firebase] signInWithGoogle error:", err?.message || err);
    throw err;
  }
}

export async function signOutUser() {
  try {
    await fbSignOut(auth);
  } catch (err) {
    console.warn("[firebase] signOut error:", err?.message || err);
    throw err;
  }
}

/* ---------------------------
   Auth subscription helpers
   --------------------------- */
export function subscribeToAuth(callback) {
  return onAuthStateChanged(auth, callback);
}

export function getCurrentUserOnce() {
  return new Promise((resolve) => {
    const unsub = onAuthStateChanged(
      auth,
      (u) => {
        unsub();
        resolve(u);
      },
      (err) => {
        unsub();
        console.error("[firebase] getCurrentUserOnce error:", err?.message || err);
        resolve(null);
      }
    );
  });
}

/* ---------------------------
   Export app
   --------------------------- */
export default app;
